import { TERMINOLOGY } from '@shared/i18n/terminology';
import { useContext } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiContext } from '@shared/contexts';
import { PROJECTS_ENDPOINT, PROJECTS_QUERY_KEY, PROJECT_QUERY_KEY } from './constants';
import { TASKS_QUERY_KEY, TASK_QUERY_KEY } from './tasks/constants';
import type {
  TProjectDetails,
  TProjectFormValues,
  TProjectList,
  TProjectListParams,
  EProjectStatus,
  TUpdateProjectDetails,
} from './types';

export const useProjects = (params: TProjectListParams) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const query = useQuery<TProjectList>({
    queryKey: [PROJECTS_QUERY_KEY, params],
    retry,
    placeholderData: (previousData, previousQuery) =>
      (previousQuery?.queryKey[1] as TProjectListParams | undefined)?.parent === params.parent
        ? keepPreviousData(previousData)
        : undefined,
    queryFn: async ({ signal }) =>
      (
        await apiClient.get<TProjectList>(PROJECTS_ENDPOINT, {
          signal,
          params: {
            ...params,
            page: params.page - 1,
            status: params.status.length ? params.status.join(',') : undefined,
          },
        })
      ).data,
  });
  useHandleQueryError(query.isError, query.error, `Loading ${TERMINOLOGY.project.lowerPlural} failed!`, true);
  return query;
};

export const useProject = (id: string, enabled: boolean) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const query = useQuery({
    queryKey: [PROJECT_QUERY_KEY, id],
    enabled,
    retry,
    queryFn: async ({ signal }) =>
      (await apiClient.get<TProjectDetails>(`${PROJECTS_ENDPOINT}/${id}`, { signal })).data,
  });
  useHandleQueryError(query.isError, query.error, `Loading the ${TERMINOLOGY.project.lower} failed!`, true);
  return query;
};

export const useCreateProject = (parent: string) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: TProjectFormValues) =>
      (await apiClient.post<string>(PROJECTS_ENDPOINT, { ...values, parent })).data,
    onError: handleApiError(`Creating the ${TERMINOLOGY.project.lower} failed!`, true),
    onSuccess: () => {
      show('success', `${TERMINOLOGY.project.upper} created.`);
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
    },
  });
};

export const useUpdateProject = (id: string) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: TUpdateProjectDetails) =>
      (await apiClient.put<TProjectDetails>(`${PROJECTS_ENDPOINT}/${id}/details`, values)).data,
    onError: (error, variables, context) => {
      handleApiError(`Updating the ${TERMINOLOGY.project.lower} failed!`, true)(error, variables, context);
      // The refusal may mean the right was lost: read the current right again.
      queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEY, id], exact: true });
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
    },
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: [PROJECT_QUERY_KEY, data.project._id], exact: true });
      queryClient.setQueryData([PROJECT_QUERY_KEY, data.project._id], data);
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
      // Task permissions come from the Project's leads and members, which the server derives on each read.
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY, { project: data.project._id }] });
      queryClient.invalidateQueries({ queryKey: [TASK_QUERY_KEY] });
      show('success', `${TERMINOLOGY.project.upper} updated.`);
    },
  });
};

export const useSetProjectStatus = (id: string) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (status: EProjectStatus) =>
      (await apiClient.put<TProjectDetails>(`${PROJECTS_ENDPOINT}/${id}/status`, { status })).data,
    onError: (error, variables, context) => {
      handleApiError(`Changing the ${TERMINOLOGY.project.lower} status failed!`, true)(error, variables, context);
      // The refusal may mean the right was lost: read the current right again.
      queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEY, id], exact: true });
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
    },
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: [PROJECT_QUERY_KEY, data.project._id], exact: true });
      queryClient.setQueryData([PROJECT_QUERY_KEY, data.project._id], data);
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
      show('success', `${TERMINOLOGY.project.upper} status changed.`);
    },
  });
};

export const useArchiveProject = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`${PROJECTS_ENDPOINT}/${id}`),
    onError: (error, id, context) => {
      handleApiError(`Archiving the ${TERMINOLOGY.project.lower} failed!`, true)(error, id, context);
      // The refusal may mean the right was lost: read the current right again.
      queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEY, id], exact: true });
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: [PROJECT_QUERY_KEY, id], exact: true });
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
      show('success', `${TERMINOLOGY.project.upper} archived.`);
    },
  });
};
